import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Button,
  CircularProgress,
  Container,
} from '@mui/material';
import { CheckCircle, RadioButtonUnchecked, Business } from '@mui/icons-material';
import { UnitAssignment } from '../types/auth';
import { apiService } from '../services/api';

interface UnitSelectionProps {
  availableUnits: UnitAssignment[];
  onUnitSelected: () => void;
  onError: (error: string) => void;
}

const UnitSelection: React.FC<UnitSelectionProps> = ({
  availableUnits,
  onUnitSelected,
  onError,
}) => {
  const [selectedUnitName, setSelectedUnitName] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSelectUnit = async () => {
    if (!selectedUnitName) {
      onError('Please select a unit');
      return;
    }

    setLoading(true);
    try {
      await apiService.selectUnit(selectedUnitName);
      onUnitSelected();
    } catch (err: any) {
      console.error('Unit selection error:', err);
      onError(err.message || 'Failed to select unit');
      setLoading(false);
    }
  };

  const handleUnitClick = (unit: UnitAssignment) => {
    setSelectedUnitName(unit.unit);
  };

  const isUnitSelected = (unit: UnitAssignment) => {
    return selectedUnitName === unit.unit;
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          py: 4,
        }}
      >
        <Card elevation={3} sx={{ width: '100%' }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Business sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
              <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
                Select Your Unit
              </Typography>
              <Typography variant="body2" color="text.secondary">
                You have access to multiple units. Please select one to continue.
              </Typography>
            </Box>

            <List sx={{ width: '100%' }}>
              {availableUnits.map((unit, index) => (
                <ListItem
                  key={`${unit.tenant}-${unit.solution}-${unit.unit}-${index}`}
                  disablePadding
                  sx={{ mb: 1 }}
                >
                  <ListItemButton
                    onClick={() => handleUnitClick(unit)}
                    selected={isUnitSelected(unit)}
                    sx={{
                      border: 1,
                      borderColor: isUnitSelected(unit) ? 'primary.main' : 'divider',
                      borderRadius: 1,
                      '&.Mui-selected': {
                        bgcolor: 'primary.light',
                        '&:hover': {
                          bgcolor: 'primary.light',
                        },
                      },
                    }}
                  >
                    <ListItemIcon>
                      {isUnitSelected(unit) ? (
                        <CheckCircle color="primary" />
                      ) : (
                        <RadioButtonUnchecked />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={unit.unit}
                      secondary={`${unit.tenant} · ${unit.solution}`}
                      primaryTypographyProps={{ fontWeight: 'medium' }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>

            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={handleSelectUnit}
              disabled={!selectedUnitName || loading}
              sx={{ mt: 3 }}
              startIcon={loading ? <CircularProgress size={20} /> : null}
            >
              {loading ? 'Confirming...' : 'Confirm Selection'}
            </Button>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
};

export default UnitSelection;
